import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Fish,
  Plus,
  Pencil,
  Trash,
  Clock,
  CurrencyEur,
  CalendarDots,
  ChartLine,
  FishSimple,
  Images,
  SignOut,
  House,
  Receipt,
  List,
  X
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminTripsPage = () => {
  const { token, logout } = useAuth();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [formData, setFormData] = useState({
    name_fr: '',
    name_en: '',
    name_it: '',
    description_fr: '',
    description_en: '',
    description_it: '',
    duration_hours: 2,
    price_per_person: 90,
    privatization_price: 360,
    max_participants: 4,
    image_url: '',
    time_slots: [{ start_time: '08:00', end_time: '10:00', label: '8H-10H' }],
    is_active: true,
    season_start: '04-01',
    season_end: '09-30',
    duration_options: []
  });

  const fetchTrips = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/trips`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrips(response.data);
    } catch (error) {
      console.error('Error fetching trips:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTrips();
  }, [fetchTrips]);

  const handleSaveTrip = async () => {
    try {
      if (selectedTrip) {
        await axios.put(`${API}/admin/trips/${selectedTrip.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Sortie modifiée avec succès');
      } else {
        await axios.post(`${API}/admin/trips`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Sortie créée avec succès');
      }
      setEditDialogOpen(false);
      setSelectedTrip(null);
      resetForm();
      fetchTrips();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteTrip = async () => {
    if (!selectedTrip) return;
    try {
      await axios.delete(`${API}/admin/trips/${selectedTrip.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sortie supprimée avec succès');
      setDeleteDialogOpen(false);
      setSelectedTrip(null);
      fetchTrips();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const openEditDialog = (trip = null) => {
    if (trip) {
      setSelectedTrip(trip);
      setFormData({
        name_fr: trip.name_fr || '',
        name_en: trip.name_en || '',
        name_it: trip.name_it || '',
        description_fr: trip.description_fr || '',
        description_en: trip.description_en || '',
        description_it: trip.description_it || '',
        duration_hours: trip.duration_hours || 2,
        price_per_person: trip.price_per_person || 90,
        privatization_price: trip.privatization_price || 360,
        max_participants: trip.max_participants || 4,
        image_url: trip.image_url || '',
        time_slots: trip.time_slots || [{ start_time: '08:00', end_time: '10:00', label: '8H-10H' }],
        is_active: trip.is_active ?? true,
        season_start: trip.season_start || '04-01',
        season_end: trip.season_end || '09-30',
        duration_options: trip.duration_options || []
      });
    } else {
      setSelectedTrip(null);
      resetForm();
    }
    setEditDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name_fr: '',
      name_en: '',
      name_it: '',
      description_fr: '',
      description_en: '',
      description_it: '',
      duration_hours: 2,
      price_per_person: 90,
      privatization_price: 360,
      max_participants: 4,
      image_url: '',
      time_slots: [{ start_time: '08:00', end_time: '10:00', label: '8H-10H' }],
      is_active: true,
      season_start: '04-01',
      season_end: '09-30',
      duration_options: []
    });
  };

  const addTimeSlot = () => {
    setFormData({
      ...formData,
      time_slots: [...formData.time_slots, { start_time: '', end_time: '', label: '' }]
    });
  };

  const removeTimeSlot = (index) => {
    setFormData({
      ...formData,
      time_slots: formData.time_slots.filter((_, i) => i !== index)
    });
  };

  const updateTimeSlot = (index, field, value) => {
    const updated = [...formData.time_slots];
    updated[index][field] = value;
    setFormData({ ...formData, time_slots: updated });
  };

  const navItems = [
    { path: '/admin/dashboard', icon: CalendarDots, label: 'Réservations' },
    { path: '/admin/invoices', icon: Receipt, label: 'Factures' },
    { path: '/admin/trips', icon: FishSimple, label: 'Sorties Pêche' },
    { path: '/admin/gallery', icon: Images, label: 'Galerie' },
    { path: '/admin/analytics', icon: ChartLine, label: 'Analytics' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-trips-page">
      {/* Sidebar */}
      <aside className="hidden lg:block fixed left-0 top-0 bottom-0 w-64 bg-[#000000] text-white">
        <div className="p-6">
          <Link to="/" className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_calvi-fishing-book/artifacts/mw45n5je_Logo%20Calvi%20Fishing.png" alt="Calvi Fishing" className="h-10 w-10 object-contain rounded-full" />
            <span className="text-lg font-bold font-['Playfair_Display']">CALVI FISHING</span>
          </Link>
        </div>
        
        <nav className="mt-6 px-3">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors ${
                item.path === '/admin/trips'
                  ? 'bg-[#b7ede0] text-white'
                  : 'text-gray-400 hover:bg-white/5 hover:text-white'
              }`}
            >
              <item.icon size={20} />
              <span className="text-sm font-medium">{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-6 space-y-2">
          <Link to="/">
            <Button variant="ghost" className="w-full justify-start text-gray-400 hover:text-white hover:bg-white/5">
              <House size={20} className="mr-3" />
              Voir le site
            </Button>
          </Link>
          <Button 
            variant="ghost" 
            onClick={logout}
            className="w-full justify-start text-gray-400 hover:text-[#FF3B30] hover:bg-red-500/10"
          >
            <SignOut size={20} className="mr-3" />
            Déconnexion
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#000000] text-white px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_calvi-fishing-book/artifacts/mw45n5je_Logo%20Calvi%20Fishing.png" alt="Calvi Fishing" className="h-8 w-8 object-contain rounded-full" />
            <span className="font-bold font-['Playfair_Display']">CALVI FISHING</span>
          </div>
          <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-white">{mobileMenuOpen ? <X size={24} /> : <List size={24} />}</Button>
        </div>
        {mobileMenuOpen && (
          <nav className="mt-3 pb-3 border-t border-white/10 pt-3 space-y-1">
            {navItems.map((item) => (
              <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg ${item.path === '/admin/trips' ? 'bg-[#b7ede0] text-[#000000] font-semibold' : 'text-gray-400'}`}>
                <item.icon size={18} /><span className="text-sm">{item.label}</span>
              </Link>
            ))}
          </nav>
        )}
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-[#000000] font-['Playfair_Display']">
            Gestion des Sorties Pêche
          </h1>
          <Button 
            onClick={() => openEditDialog()}
            className="bg-[#b7ede0] hover:bg-[#9ee5d4] text-white rounded-full gap-2"
            data-testid="add-trip-btn"
          >
            <Plus size={18} />
            Nouvelle sortie
          </Button>
        </div>

        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table className="min-w-[600px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Image</TableHead>
                  <TableHead>Nom (FR)</TableHead>
                  <TableHead>Durée</TableHead>
                  <TableHead>Prix/pers</TableHead>
                  <TableHead>Privatisation</TableHead>
                  <TableHead>Créneaux</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {trips.map((trip) => (
                  <TableRow key={trip.id} data-testid={`trip-row-${trip.id}`}>
                    <TableCell>
                      <img 
                        src={trip.image_url} 
                        alt={trip.name_fr}
                        className="w-16 h-12 object-cover rounded"
                      />
                    </TableCell>
                    <TableCell className="font-medium">{trip.name_fr}</TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        {trip.duration_options && trip.duration_options.length > 0
                          ? trip.duration_options.map(o => o.label || `${o.duration_hours}h`).join(' / ')
                          : `${trip.duration_hours}h`}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1">
                        <CurrencyEur size={14} />
                        {trip.duration_options && trip.duration_options.length > 0
                          ? trip.duration_options.map(o => `${o.price_per_person}€`).join(' / ')
                          : `${trip.price_per_person}€`}
                      </span>
                    </TableCell>
                    <TableCell>
                      {trip.duration_options && trip.duration_options.length > 0
                        ? trip.duration_options.map(o => `${o.privatization_price}€`).join(' / ')
                        : `${trip.privatization_price}€`}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {trip.time_slots.map((slot, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {slot.label}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={trip.is_active ? 'default' : 'secondary'}>
                        {trip.is_active ? 'Actif' : 'Inactif'}
                      </Badge>
                      {trip.season_start && trip.season_end && (
                        <div className="text-xs text-[#4B5563] mt-1">
                          {trip.season_start.replace('-', '/')} au {trip.season_end.replace('-', '/')}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(trip)}
                          data-testid={`edit-trip-${trip.id}`}
                        >
                          <Pencil size={14} />
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => {
                            setSelectedTrip(trip);
                            setDeleteDialogOpen(true);
                          }}
                          data-testid={`delete-trip-${trip.id}`}
                        >
                          <Trash size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </main>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedTrip ? 'Modifier la sortie' : 'Nouvelle sortie'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Names */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Nom (FR)</Label>
                <Input
                  value={formData.name_fr}
                  onChange={(e) => setFormData({ ...formData, name_fr: e.target.value })}
                  placeholder="Pêche Découverte"
                />
              </div>
              <div>
                <Label>Nom (EN)</Label>
                <Input
                  value={formData.name_en}
                  onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                  placeholder="Discovery Fishing"
                />
              </div>
              <div>
                <Label>Nom (IT)</Label>
                <Input
                  value={formData.name_it}
                  onChange={(e) => setFormData({ ...formData, name_it: e.target.value })}
                  placeholder="Pesca Scoperta"
                />
              </div>
            </div>

            {/* Descriptions */}
            <div>
              <Label>Description (FR)</Label>
              <Textarea
                value={formData.description_fr}
                onChange={(e) => setFormData({ ...formData, description_fr: e.target.value })}
                rows={3}
              />
            </div>
            <div>
              <Label>Description (EN)</Label>
              <Textarea
                value={formData.description_en}
                onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                rows={3}
              />
            </div>
            <div>
              <Label>Description (IT)</Label>
              <Textarea
                value={formData.description_it}
                onChange={(e) => setFormData({ ...formData, description_it: e.target.value })}
                rows={3}
              />
            </div>

            {/* Pricing */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <Label>Durée (heures)</Label>
                <Input
                  type="number"
                  value={formData.duration_hours}
                  onChange={(e) => setFormData({ ...formData, duration_hours: parseFloat(e.target.value) })}
                />
              </div>
              <div>
                <Label>Prix/personne (€)</Label>
                <Input
                  type="number"
                  value={formData.price_per_person}
                  onChange={(e) => setFormData({ ...formData, price_per_person: parseFloat(e.target.value) })}
                />
              </div>
              <div>
                <Label>Privatisation (€)</Label>
                <Input
                  type="number"
                  value={formData.privatization_price}
                  onChange={(e) => setFormData({ ...formData, privatization_price: parseFloat(e.target.value) })}
                />
              </div>
              <div>
                <Label>Max participants</Label>
                <Input
                  type="number"
                  value={formData.max_participants}
                  onChange={(e) => setFormData({ ...formData, max_participants: parseInt(e.target.value) })}
                />
              </div>
            </div>

            {/* Duration Options (multi-duration trips like Pêche au Gros 4H/6H) */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="font-medium">Options de durée (optionnel)</Label>
                <Button type="button" variant="outline" size="sm" className="text-xs gap-1"
                  onClick={() => setFormData({ ...formData, duration_options: [...formData.duration_options, { label: '', duration_hours: 4, price_per_person: 100, privatization_price: 400 }] })}>
                  <Plus size={14} />Ajouter une durée
                </Button>
              </div>
              {formData.duration_options.length > 0 && (
                <p className="text-xs text-[#6B7280] mb-2">Quand des options de durée existent, le client pourra choisir entre les différentes durées sur la page de réservation.</p>
              )}
              {formData.duration_options.map((opt, idx) => (
                <div key={idx} className="grid grid-cols-5 gap-2 mb-2 items-end">
                  <div>
                    <Label className="text-xs">Label</Label>
                    <Input value={opt.label} placeholder="ex: 4H" onChange={(e) => {
                      const updated = [...formData.duration_options];
                      updated[idx] = { ...updated[idx], label: e.target.value };
                      setFormData({ ...formData, duration_options: updated });
                    }} />
                  </div>
                  <div>
                    <Label className="text-xs">Durée (h)</Label>
                    <Input type="number" value={opt.duration_hours} onChange={(e) => {
                      const updated = [...formData.duration_options];
                      updated[idx] = { ...updated[idx], duration_hours: parseFloat(e.target.value) };
                      setFormData({ ...formData, duration_options: updated });
                    }} />
                  </div>
                  <div>
                    <Label className="text-xs">Prix/pers (€)</Label>
                    <Input type="number" value={opt.price_per_person} onChange={(e) => {
                      const updated = [...formData.duration_options];
                      updated[idx] = { ...updated[idx], price_per_person: parseFloat(e.target.value) };
                      setFormData({ ...formData, duration_options: updated });
                    }} />
                  </div>
                  <div>
                    <Label className="text-xs">Privatisation (€)</Label>
                    <Input type="number" value={opt.privatization_price} onChange={(e) => {
                      const updated = [...formData.duration_options];
                      updated[idx] = { ...updated[idx], privatization_price: parseFloat(e.target.value) };
                      setFormData({ ...formData, duration_options: updated });
                    }} />
                  </div>
                  <Button type="button" variant="ghost" size="sm" className="text-red-500 hover:bg-red-50"
                    onClick={() => setFormData({ ...formData, duration_options: formData.duration_options.filter((_, i) => i !== idx) })}>
                    <Trash size={16} />
                  </Button>
                </div>
              ))}
            </div>

            {/* Image URL */}
            <div>
              <Label>URL de l'image</Label>
              <Input
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                placeholder="https://..."
              />
            </div>

            {/* Time Slots */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Créneaux horaires</Label>
                <Button variant="outline" size="sm" onClick={addTimeSlot}>
                  <Plus size={14} className="mr-1" /> Ajouter
                </Button>
              </div>
              <div className="space-y-2">
                {formData.time_slots.map((slot, index) => (
                  <div key={index} className="flex gap-2 items-center">
                    <Input
                      type="time"
                      value={slot.start_time}
                      onChange={(e) => updateTimeSlot(index, 'start_time', e.target.value)}
                      className="w-32"
                    />
                    <span>à</span>
                    <Input
                      type="time"
                      value={slot.end_time}
                      onChange={(e) => updateTimeSlot(index, 'end_time', e.target.value)}
                      className="w-32"
                    />
                    <Input
                      value={slot.label}
                      onChange={(e) => updateTimeSlot(index, 'label', e.target.value)}
                      placeholder="8H-10H"
                      className="flex-1"
                    />
                    {formData.time_slots.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removeTimeSlot(index)}>
                        <Trash size={14} className="text-[#FF3B30]" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label>Sortie active</Label>
            </div>

            {/* Season Dates */}
            <div className="bg-[#F8FAFC] rounded-lg p-4 space-y-3">
              <Label className="font-semibold text-base">Période de diffusion</Label>
              <p className="text-xs text-[#4B5563]">Dates de début et fin de saison (chaque année)</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Début de saison</Label>
                  <select
                    value={formData.season_start || '04-01'}
                    onChange={(e) => setFormData({ ...formData, season_start: e.target.value })}
                    className="w-full mt-1 rounded-lg border border-[#E2E8F0] p-2 text-sm"
                  >
                    <option value="01-01">1er Janvier</option>
                    <option value="02-01">1er Février</option>
                    <option value="03-01">1er Mars</option>
                    <option value="03-15">15 Mars</option>
                    <option value="04-01">1er Avril</option>
                    <option value="04-15">15 Avril</option>
                    <option value="05-01">1er Mai</option>
                    <option value="05-15">15 Mai</option>
                    <option value="06-01">1er Juin</option>
                  </select>
                </div>
                <div>
                  <Label className="text-sm">Fin de saison</Label>
                  <select
                    value={formData.season_end || '09-30'}
                    onChange={(e) => setFormData({ ...formData, season_end: e.target.value })}
                    className="w-full mt-1 rounded-lg border border-[#E2E8F0] p-2 text-sm"
                  >
                    <option value="08-31">31 Août</option>
                    <option value="09-15">15 Septembre</option>
                    <option value="09-30">30 Septembre</option>
                    <option value="10-15">15 Octobre</option>
                    <option value="10-31">31 Octobre</option>
                    <option value="11-15">15 Novembre</option>
                    <option value="11-30">30 Novembre</option>
                    <option value="12-31">31 Décembre</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveTrip}>
              {selectedTrip ? 'Enregistrer' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer la sortie</DialogTitle>
          </DialogHeader>
          <p className="text-[#4B5563]">
            Êtes-vous sûr de vouloir supprimer cette sortie ? Cette action est irréversible.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleDeleteTrip}>
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTripsPage;
